import { Button } from "@shared/components/ui/Button";
import EditSectionButton from "@shared/components/ui/EditSectionButton";
import Shimmer from "@shared/components/ui/Shimmer";
import React from "react";
import { SectionId } from "../../types/home.types";
import { Trophy } from "lucide-react";
import EventSection from "../EventSection";
import GettingStartedSection from "../GettingStartedSection";
import LeaderboardSection from "../LeaderboardSection";
import QuoteSection from "../QuoteSection";
import RecordingsSection from "../RecordingsSection";

interface SectionRendererProps {
  sectionId: SectionId;
  isLoading: boolean;
  isAdmin: boolean;
  customClasses?: string;
  homePageData: any;
  defaultData?: any;
  onEditClick: (section: string) => void;
}

/**
 * Component that dynamically renders a section based on its ID
 */
export const SectionRenderer: React.FC<SectionRendererProps> = ({
  sectionId,
  isLoading,
  isAdmin,
  customClasses = "",
  homePageData,
  defaultData,
  onEditClick,
}) => {
  // Shimmer component for loading state
  const ShimmerBlock = ({ height = 400 }: { height?: number }) => (
    <Shimmer height={height} />
  );

  // Render different sections based on sectionId
  const renderSection = () => {
    if (isLoading) {
      return <ShimmerBlock />;
    }

    switch (sectionId) {
      case "gettingStarted":
        return (
          <div className={`relative h-full ${customClasses}`}>
            {isAdmin && (
              <div className="absolute top-2 right-2 z-10">
                <EditSectionButton
                  onClick={() => onEditClick("gettingStarted")}
                />
              </div>
            )}
            <GettingStartedSection
              title={homePageData?.gettingStartedSection?.title}
              description={homePageData?.gettingStartedSection?.description}
              buttonText={homePageData?.gettingStartedSection?.buttonText}
              buttonLink={homePageData?.gettingStartedSection?.buttonLink}
              buttonTarget={homePageData?.gettingStartedSection?.buttonTarget}
            />
          </div>
        );

      case "event":
        return (
          <div className={`relative h-full flex flex-col ${customClasses}`}>
            {isAdmin && (
              <div className="absolute top-4 right-4 z-10">
                <EditSectionButton onClick={() => onEditClick("event")} />
              </div>
            )}
            <EventSection
              title={homePageData?.eventSection?.title || defaultData?.title}
              subtitle={
                homePageData?.eventSection?.subtitle || defaultData?.subtitle
              }
              description={
                homePageData?.eventSection?.description ||
                defaultData?.description
              }
              buttonText={
                homePageData?.eventSection?.buttonText ||
                defaultData?.buttonText
              }
              buttonLink={
                homePageData?.eventSection?.buttonLink ||
                defaultData?.buttonLink
              }
              buttonTarget={homePageData?.eventSection?.buttonTarget}
              imagePath={
                homePageData?.eventSection?.imagePath || defaultData?.imagePath
              }
              videoPath={homePageData?.eventSection?.videoPath}
              mediaType={homePageData?.eventSection?.mediaType}
              roundedImage={homePageData?.eventSection?.roundedImage ?? true}
              videoAutoplay={homePageData?.eventSection?.videoAutoplay ?? false}
              videoLoop={homePageData?.eventSection?.videoLoop ?? false}
              videoMuted={homePageData?.eventSection?.videoMuted ?? false}
            />
          </div>
        );

      case "quote":
        return (
          <div className={`relative h-full w-full`}>
            {isAdmin && (
              <div className="absolute top-4 right-4 z-10">
                <EditSectionButton onClick={() => onEditClick("quote")} />
              </div>
            )}
            <QuoteSection
              quote={homePageData?.quoteSection?.quote || defaultData?.quote}
              person={homePageData?.quoteSection?.person || defaultData?.person}
            />
          </div>
        );

      case "leaderboard":
        return (
          <div className="relative h-full">
            {isAdmin && (
              <div className="absolute top-2 right-2 z-10">
                <EditSectionButton onClick={() => onEditClick("leaderboard")} />
              </div>
            )}
            <LeaderboardSection
              title={homePageData?.leaderboardSection?.title}
              subtitle={homePageData?.leaderboardSection?.subtitle}
              logoUrl={homePageData?.leaderboardSection?.logoUrl}
              showPoints={homePageData?.leaderboardSection?.showPoints}
              showEmail={homePageData?.leaderboardSection?.showEmail}
              topN={homePageData?.leaderboardSection?.topN}
              theme={homePageData?.leaderboardSection?.theme}
              entries={homePageData?.leaderboardSection?.entries}
            />
          </div>
        );

      case "recordings":
        return (
          <div className={`relative h-full`}>
            {isAdmin && (
              <div className="absolute top-2 right-2 z-10 flex gap-2">
                {!homePageData?.leaderboardSection?.enabled && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEditClick("leaderboard")}
                    className="bg-white bg-opacity-70 hover:bg-opacity-100"
                    title="Show a leaderboard here and move the recordings below"
                    aria-label="Leaderboard"
                  >
                    <Trophy className="h-4 w-4" />
                  </Button>
                )}
                <EditSectionButton onClick={() => onEditClick("recordings")} />
              </div>
            )}
            {homePageData?.recordingsSection && (
              <RecordingsSection
                title={homePageData?.recordingsSection?.title}
                recordings={homePageData?.recordingsSection?.recordings}
                viewAllText={homePageData?.recordingsSection?.viewAllText}
                viewAllLink={homePageData?.recordingsSection?.viewAllLink}
                viewAllTarget={homePageData?.recordingsSection?.viewAllTarget}
                fullWidth={homePageData?.leaderboardSection?.enabled}
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return renderSection();
};
